<!-- Личная история: загрузка из store, состояния списка и серверная пагинация. -->
<template>
  <section tabindex="-1" aria-label="Список уведомлений" :aria-busy="store.dataIsLoading">
    <BaseTooltip ref="feedbackTooltip" class="notification-list__feedback" />
    <component
      :is="embedded ? 'div' : 'BaseNav'"
      v-if="!compact"
      v-bind="embedded ? {} : { title: 'Уведомления' }"
      :class="{ 'notification-list__actions': embedded }"
    >
      <base-button
        variant="primary"
        icon="check"
        :disabled="store.isReading || store.dataIsLoading || store.loadFailed || !hasUnread"
        @click="markRead(null, $event)"
      >
        {{ store.readingAll ? "Отмечаем…" : hasUnread ? "Прочитать все" : "Все прочитано" }}
      </base-button>
    </component>
    <Navigation v-if="!compact" :embedded="embedded" />
    <BasePageHint v-if="!compact">События по вашим подпискам в текущей компании.</BasePageHint>
    <OtherCompanies v-if="!compact" />

    <base-loader v-if="store.dataIsLoading" />
    <BaseEmptyState v-else-if="store.loadFailed" title="Не удалось загрузить уведомления">
      <template #actions>
        <base-button @click="loadItems">Повторить</base-button>
      </template>
    </BaseEmptyState>
    <router-link
      v-else-if="compact && !store.items.length"
      class="notification-list__empty-link"
      aria-label="Непрочитанных уведомлений нет. Посмотреть все уведомления"
      :to="{ name: 'Notifications' }"
      @click="$emit('open-history')"
    >
      <BaseEmptyState
        icon="bell"
        title="Непрочитанных уведомлений нет"
        description="Нажмите, чтобы посмотреть все уведомления."
      />
    </router-link>
    <BaseEmptyState
      v-else-if="!store.items.length"
      icon="bell"
      title="Уведомлений пока нет"
      description="Здесь появятся события, на которые вы подписаны."
    />
    <template v-else>
      <section v-for="group in groups" :key="group.date || 'popup'">
        <ActivityLogDateDivider v-if="!compact" :label="dateLabel(group.items[0].created_at)" />
        <ul class="notification-list" :class="{ 'notification-list--compact': compact }" aria-label="Уведомления текущей компании">
        <ListItem
          v-for="item in group.items"
          :key="item.id"
          :data-notification-id="item.id"
          :item="item"
          :compact="compact"
          :reading="store.readingIds.includes(item.id)"
          :disabled="store.readingAll || store.readingIds.includes(item.id)"
          @mark-read="markRead(item.id, $event)"
          @mark-unread="markUnread(item.id, $event)"
        />
        </ul>
      </section>
      <div v-if="!compact && store.hasMore" ref="loadMoreTrigger" class="notification-list__load-more">
        <base-loader v-if="store.isLoadingMore" />
        <base-button v-else @click="loadMore">
          {{ store.loadMoreFailed ? 'Повторить загрузку' : 'Показать ещё' }}
        </base-button>
      </div>
    </template>
  </section>
</template>

<script>
import ListItem from "@/components/Notification/List/Item.vue";
import Navigation from "@/components/Notification/Navigation.vue";
import OtherCompanies from "@/components/Notification/OtherCompanies.vue";
import ActivityLogDateDivider from "@/components/ActivityLog/ActivityLogDateDivider.vue";
import { currentDateOnly, formatActivityLogDateLabel } from "@/utils/dateUtils";
import { createNotificationFocusRestorer } from "@/utils/notification/focusUtils";
import { useNotificationItemsStore, useNotificationPopupStore } from "@/stores/notificationItemsStore";
import { useNotificationCounterStore } from "@/stores/notificationCounterStore";
import { useUserDataStore } from "@/stores/userDataStore";

export default {
  name: "NotificationList",
  components: { ListItem, Navigation, OtherCompanies, ActivityLogDateDivider },
  emits: ["open-history"],
  props: {
    compact: { type: Boolean, default: false },
    embedded: { type: Boolean, default: false },
  },
  data() {
    return { loadMoreObserver: null, isMounted: false };
  },
  computed: {
    store() {
      return this.compact ? useNotificationPopupStore() : useNotificationItemsStore();
    },
    hasUnread() {
      return useNotificationCounterStore().unreadCount > 0 || this.store.items.some((item) => !item.read_at);
    },
    groups() {
      return this.compact ? [{ date: null, items: this.store.items }] : this.store.groups;
    },
    requestContext() {
      const user = useUserDataStore().userData;
      return `${user?.id}:${user?.current_company?.id}`;
    },
  },
  watch: {
    requestContext: {
      immediate: true,
      handler() {
        this.loadItems();
      },
    },
    "store.items"() {
      this.preserveScrollPosition();
      this.setupLoadMoreObserver();
    },
    "store.isLoadingMore"() {
      this.setupLoadMoreObserver();
    },
    "store.isRefreshing"() {
      this.setupLoadMoreObserver();
    },
  },
  mounted() {
    this.isMounted = true;
    this.setupLoadMoreObserver();
  },
  beforeUnmount() {
    this.isMounted = false;
    this.loadMoreObserver?.disconnect();
    this.store.reset();
  },
  methods: {
    async markRead(id = null, event) {
      // Карточка исчезает из popup после прочтения; сохраняем место нажатия заранее.
      const anchor = event?.currentTarget?.getBoundingClientRect();
      const restoreFocus = createNotificationFocusRestorer(event?.currentTarget, this.$el);
      const saved = await this.store.markRead(id);
      await this.$nextTick();
      restoreFocus();
      if (saved) {
        useNotificationCounterStore().fetchCount();
        this.$refs.feedbackTooltip?.showFeedback(
          id === null ? "Уведомления прочитаны" : "Прочитано", anchor
        );
      }
    },
    async markUnread(id, event) {
      const anchor = event?.currentTarget?.getBoundingClientRect();
      const restoreFocus = createNotificationFocusRestorer(event?.currentTarget, this.$el);
      const saved = await this.store.markUnread(id);
      await this.$nextTick();
      restoreFocus();
      if (saved) {
        useNotificationCounterStore().fetchCount();
        this.$refs.feedbackTooltip?.showFeedback("Отмечено непрочитанным", anchor);
      }
    },
    loadItems() {
      return this.store.openPage();
    },
    dateLabel(createdAt) {
      return formatActivityLogDateLabel(createdAt, currentDateOnly());
    },
    loadMore() {
      return this.store.loadMore();
    },
    preserveScrollPosition() {
      if (this.compact || !this.isMounted || this.store.dataIsLoading || !this.store.items.length) return;
      const container = this.$el.closest(this.embedded ? '.base-modal__body' : '.content');
      if (!container || container.scrollTop === 0) return;
      const top = container.getBoundingClientRect().top;
      const anchor = [...this.$el.querySelectorAll('[data-notification-id]')]
        .find((element) => element.getBoundingClientRect().bottom > top);
      if (!anchor) return;
      const offset = anchor.getBoundingClientRect().top;
      this.$nextTick(() => {
        if (this.isMounted && anchor.isConnected) {
          container.scrollTop += anchor.getBoundingClientRect().top - offset;
        }
      });
    },
    async setupLoadMoreObserver() {
      this.loadMoreObserver?.disconnect();
      await this.$nextTick();
      this.loadMoreObserver?.disconnect();
      if (!this.isMounted || this.compact || this.store.loadMoreFailed ||
          this.store.isLoadingMore || this.store.isRefreshing || !this.$refs.loadMoreTrigger || !window.IntersectionObserver) return;

      this.loadMoreObserver = new IntersectionObserver(([entry]) => {
        if (entry.isIntersecting) this.loadMore();
      }, {
        root: this.embedded ? this.$el.closest('.base-modal__body') : null,
        rootMargin: "300px 0px",
      });
      this.loadMoreObserver.observe(this.$refs.loadMoreTrigger);
    },
  },
};
</script>

<style scoped>
.notification-list__actions {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 16px;
}

.notification-list__feedback {
  position: absolute;
}

.notification-list {
  display: grid;
  gap: 8px;
  list-style: none;
  margin: 0;
  padding: 0;
}

.notification-list--compact {
  gap: 0;
}

.notification-list__load-more {
  min-height: 48px;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 12px;
}

.notification-list__empty-link {
  display: block;
  border-radius: 8px;
  text-decoration: none;
}

.notification-list__empty-link:focus-visible {
  outline: 2px solid var(--color-green);
  outline-offset: 2px;
}
</style>
